// src/pages/ResultsUser.tsx
import React, { useEffect, useMemo, useState } from "react";
import Header from "components/Header";
import Footer from "components/Footer";

// ===== APIベース =====
const RAW_API_BASE =
  (import.meta as any).env?.VITE_API_BASE_URL ||
  (process as any).env?.REACT_APP_API_BASE_URL ||
  "";

const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api\/?$/, "");
const API_PUBLIC = API_BASE ? `${API_BASE}/api/public` : "/api/public";

const PAGE_SIZE = 10;

// ===== サーバーの生データ =====
type RawResult = {
  id: number | string;
  date?: string;
  raceName?: string;
  title?: string;
  item?: string; // 例: "3連単 1-4-2"
  body?: string;
  amount?: number | string;
  badge?: string;
  publish?: boolean;
  imageUrl?: string;
  planName?: string;
  sort?: number | string;
};

// ===== 画面用データ =====
type Row = {
  id: number | string;
  date: string;
  race: string;
  amount: number;
  bet: string;
  isPublished: boolean;
  planName?: string;
  sort?: number;
};

const BG_RESULT = "/assets/member/new-jisseki.webp";
const HIT_ICON = "/assets/member/的中.png";

export default function ResultsUser() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_PUBLIC}/results`, { credentials: "include" });
        const b = await r.json();

        const rawList: RawResult[] = Array.isArray(b?.items)
          ? b.items
          : Array.isArray(b)
          ? b
          : [];

        const normalized: Row[] = rawList
          .map((it) => {
            const amountNum =
              typeof it.amount === "string"
                ? Number(it.amount) || 0
                : Number(it.amount ?? 0) || 0;

            const sortNum =
              typeof it.sort === "string"
                ? Number(it.sort) || 0
                : Number(it.sort ?? 0) || 0;

            return {
              id: it.id,
              date: it.date || "",
              race: it.raceName || it.title || "",
              amount: amountNum,
              bet: it.item || it.body || "",
              isPublished: it.publish !== false,
              planName: it.planName || "",
              sort: sortNum,
            };
          })
          .filter((x) => x.isPublished)
          .sort((a, b) => {
            // sort 降順 → 日付降順 → id 降順
            const sa = a.sort ?? 0;
            const sb = b.sort ?? 0;
            if (sb !== sa) return sb - sa;

            const ta = a.date ? new Date(a.date).getTime() : 0;
            const tb = b.date ? new Date(b.date).getTime() : 0;
            if (tb !== ta) return tb - ta;

            return String(b.id).localeCompare(String(a.id));
          });

        setRows(normalized);
        setPage(1);
      } catch (e) {
        console.error(e);
        setRows([]);
        setPage(1);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalPages = useMemo(() => {
    return rows.length ? Math.ceil(rows.length / PAGE_SIZE) : 1;
  }, [rows.length]);

  const safePage = Math.min(Math.max(page, 1), totalPages);
  const pageRows = useMemo(() => {
    const startIndex = (safePage - 1) * PAGE_SIZE;
    return rows.slice(startIndex, startIndex + PAGE_SIZE);
  }, [rows, safePage]);

  return (
    <>
      <Header />

      <main style={{ maxWidth: 720, margin: "20px auto", padding: "0 12px" }}>
        <h2 style={{ marginBottom: 12, fontWeight: 900 }}>BOATOKUMAN 的中実績一覧</h2>

        {/* このページ専用CSS（最小） */}
        <style>{`
          .resGrid { display: grid; gap: 14px; }

          .resCard {
            position: relative;
            border-radius: 16px;
            overflow: hidden;
            background-image: url(${BG_RESULT});
            background-size: cover;
            background-position: center;
            min-height: 132px;
          }

          /* 読みやすさ確保：暗めオーバーレイ（上を濃く） */
          .resCard::before {
            content: "";
            position: absolute;
            inset: 0;
            background: linear-gradient(
              180deg,
              rgba(0,0,0,.58) 0%,
              rgba(0,0,0,.40) 40%,
              rgba(0,0,0,.52) 100%
            );
            pointer-events: none;
          }

          .resInner {
            position: relative;
            padding: 14px 14px 18px;
            color: #fff;
            text-shadow: 0 1px 10px rgba(0,0,0,.35);
          }

          .hitIcon {
            position: absolute;
            top: 8px;
            right: 8px;
            width: 42px;
            height: 42px;
            object-fit: contain;
            pointer-events: none;
            filter: drop-shadow(0 6px 10px rgba(0,0,0,.35));
          }

          .planLine {
            font-weight: 900;
            font-size: 18px;
            letter-spacing: .01em;
            margin-bottom: 6px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .raceLine {
            font-weight: 900;
            font-size: 14px;
            line-height: 1.25;
            margin-bottom: 8px;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            max-width: calc(100% - 170px); /* 右下金額の領域を空ける */
          }

          /* ★ 日付・買い目をレース名と同等サイズへ */
          .metaRow {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            align-items: center;
            font-weight: 900;
            font-size: 14px; /* ★レース名と同等 */
            opacity: .98;
            margin-bottom: 6px;
            max-width: calc(100% - 170px); /* 右下金額の領域を空ける */
          }

          .metaDate { white-space: nowrap; }
          .metaBet {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 100%;
          }

          /* ===== 金額：右下 / ゴールド太字 / 点滅（★もっと大きく） ===== */
          @keyframes goldBlinkAmount {
            0%   { opacity: .55; filter: drop-shadow(0 2px 10px rgba(0,0,0,.35)); }
            50%  { opacity: 1;  filter: drop-shadow(0 3px 18px rgba(248,215,117,.65)); }
            100% { opacity: .55; filter: drop-shadow(0 2px 10px rgba(0,0,0,.35)); }
          }

          .amountBR {
            position: absolute;
            right: 14px;
            bottom: 12px;
            z-index: 2;

            font-weight: 900;
            font-size: 28px; /* ★もっと大きく */
            letter-spacing: .01em;

            color: rgba(248, 215, 117, 0.98);
            text-shadow:
              0 2px 18px rgba(0,0,0,.55),
              0 1px 0 rgba(255,255,255,.10);

            animation: goldBlinkAmount 2.2s ease-in-out infinite;
            white-space: nowrap;
          }

          /* 画面が狭いときは少しだけ下げる（被りにくく） */
          @media (max-width: 360px) {
            .amountBR { font-size: 26px; right: 12px; bottom: 10px; }
            .raceLine, .metaRow { max-width: calc(100% - 155px); }
          }

          @media (min-width: 480px) {
            .planLine { font-size: 19px; }
            .raceLine { font-size: 15px; }
            .metaRow  { font-size: 15px; }
            .amountBR { font-size: 32px; right: 16px; bottom: 14px; } /* ★さらに大きく */
            .raceLine, .metaRow { max-width: calc(100% - 190px); }
          }

          .empty { opacity: .7; }
        `}</style>

        {!loading && (
          <>
            <div className="resGrid">
              {pageRows.map((it) => {
                return (
                  <div key={it.id} className="resCard">
                    <img className="hitIcon" src={HIT_ICON} alt="的中" />

                    <div className="resInner">
                      {/* プラン名 */}
                      {it.planName ? (
                        <div className="planLine">{it.planName}</div>
                      ) : (
                        <div className="planLine" style={{ opacity: 0.92 }}>
                          的中実績
                        </div>
                      )}

                      {/* レース名 */}
                      <div className="raceLine">{it.race}</div>

                      {/* 日付 + 買い目（同等サイズ） */}
                      <div className="metaRow">
                        {it.date ? <span className="metaDate">{it.date}</span> : null}
                        {it.bet ? <span className="metaBet">{it.bet}</span> : null}
                      </div>

                      {/* 金額：右下（大きく） */}
                      <div className="amountBR">
                        {it.amount.toLocaleString("ja-JP")} 円 的中
                      </div>
                    </div>
                  </div>
                );
              })}

              {!pageRows.length && (
                <div className="empty">公開中の実績はまだありません。</div>
              )}
            </div>

            {rows.length > PAGE_SIZE && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 12,
                  marginTop: 16,
                }}
              >
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    background: safePage <= 1 ? "#f5f5f5" : "#fff",
                    cursor: safePage <= 1 ? "default" : "pointer",
                  }}
                >
                  前へ
                </button>

                <span style={{ fontSize: 13 }}>
                  {safePage} / {totalPages}
                </span>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    background: safePage >= totalPages ? "#f5f5f5" : "#fff",
                    cursor: safePage >= totalPages ? "default" : "pointer",
                  }}
                >
                  次へ
                </button>
              </div>
            )}
          </>
        )}

        {loading && <div style={{ opacity: 0.7 }}>読み込み中…</div>}
      </main>

      <Footer />
    </>
  );
}